import React, { Fragment, useEffect, useMemo, } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'reactstrap';
import { isEmpty, pick, fromPairs, chunk, groupBy, } from 'lodash';
import { setDoc, updateDoc, limit, query, where, orderBy, collection, doc, } from 'firebase/firestore';
import { httpsCallable, } from 'firebase/functions';
import { format as formatDate, addDays, startOfDay, endOfDay, parse as parseDate, } from 'date-fns';
import numeral from 'numeral';
import { keyBy } from 'lodash';
import { toast } from 'react-toastify';
import qs from 'qs';

import firebase, { db, batch, functions, getCollectionData, } from '../../firebase';
import env from '../../env';
import { fields, } from '../../shared/models/purchase';
import useQueryParams from '../hooks/useQueryParams';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import PurchaseFormModal from '../modals/PurchaseFormModal';
import QueryDateRangeSelector from '../QueryDateRangeSelector';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import AddButton from '../AddButton';
import ProgressButton from '../ProgressButton';
import TenantPage from '../TenantPage';
import useTenant from '../hooks/useTenant';
import ExpenseTabs from '../ExpenseTabs';
import ExportButton from '../ExportButton';
import ImportMappingButton from '../ImportMappingButton';

const getAdApiAuthUrl = httpsCallable(functions, 'getAdApiAuthUrl');
const pullAdProfiles = httpsCallable(functions, 'pullAdProfiles');

export default function Purchases (props) {
  return (
    <TenantPage>
      <PageContent {...props} />
    </TenantPage>
  );
};

function PageContent (props) {
  const { tenant, history, location, } = props;
  const queryParams = useQueryParams();
  const defaultInboundDateStart = addDays(new Date(), -180);
  const defaultInboundDateEnd = addDays(new Date(), -1);
  const inboundDateStart = useMemo(_ => queryParams.inboundDateRange?.[0] ? new Date(queryParams.inboundDateRange?.[0]) : defaultInboundDateStart, [queryParams.inboundDateRange?.[0]]);
  const inboundDateEnd = useMemo(_ => queryParams.inboundDateRange?.[1] ? new Date(queryParams.inboundDateRange?.[1]) : defaultInboundDateEnd, [queryParams.inboundDateRange?.[1]]);
  const sellers = useCollectionSubscription(query(collection(tenant.ref, 'sellers')));
  const sellersById = keyBy(sellers, 'id');
  const products = useCollectionSubscription(query(collection(tenant.ref, 'products'), where('amazonAttributes.isParent', '==', false)));
  const productsById = keyBy(products, 'id');
  const purchases = useCollectionSubscription(query(collection(tenant.ref, 'purchases'), where('inboundDate', '>=', startOfDay(inboundDateStart)), where('inboundDate', '<=', endOfDay(inboundDateEnd)), orderBy('inboundDate', 'desc')), [inboundDateStart, inboundDateEnd]);

  const shouldAutoOpen = queryParams.addNew === 'true' && queryParams.productId;
  const initialValues = shouldAutoOpen ? {
    items: [{ productId: queryParams.productId, }],
  } : undefined;

  const getExportData = () => {
    return purchases.flatMap(_ =>
      _.items.map(item => ({
        inboundDate: _.inboundDate ? formatDate(_.inboundDate.toDate(), 'yyyy/MM/dd') : '',
        productId: item.productId,
        productName: productsById[item.productId]?.name,
        quantity: item.quantity,
        amount: item.amount,
      }))
    );
  };

  const importFields = {
    inboundDate: {
      label: '納品日',
      type: 'date',
      validations: { required: true },
    },
    productId: {
      label: '商品ID',
      type: 'select',
      options: products.map(_ => _.id),
      validations: { required: true },
    },
    quantity: {
      label: '数量',
      type: 'integer',
      validations: { required: true },
    },
    amount: {
      label: '商品原価',
      type: 'integer',
      validations: { required: true },
    },
  };

  const handleImport = async (rows) => {
    const rowsWithParsedDate = rows.map(_ => ({
      ..._,
      inboundDate: parseDate(_.inboundDate, 'yyyy/MM/dd', new Date()),
    }));

    const purchasesByDate = groupBy(rowsWithParsedDate, _ => formatDate(_.inboundDate, 'yyyy-MM-dd'));

    const purchasesToCreate = Object.values(purchasesByDate).map(dateRows => ({
      inboundDate: dateRows[0].inboundDate,
      items: dateRows.map(_ => ({
        productId: _.productId,
        quantity: parseInt(_.quantity, 10),
        amount: parseInt(_.amount, 10),
      })),
    }));

    await batch(purchasesToCreate, (batchWrite, _) => {
      const purchaseRef = doc(collection(tenant.ref, 'purchases'));
      batchWrite.set(purchaseRef, {
        inboundDate: _.inboundDate,
        items: _.items,
        createdAt: new Date(),
      });
    });
  };

  return (
    <div className="purchases container-fluid">
      <div className="px-4">
        <div className="card p-4">
          <ExpenseTabs activeTab="purchases" />
          <div className="mb-2 d-flex justify-content-start align-items-end gap-1">
            <QueryDateRangeSelector label="納品日" defaultValue={[inboundDateStart, inboundDateEnd]} paramName="inboundDateRange" pickerProps={{ showYearDropdown: true, dropdownMode: 'select' }} />
          </div>
          <div className="mb-3 d-flex justify-content-start align-items-end gap-1">
            <AddButton itemRef={doc(collection(tenant.ref, 'purchases'))} FormModal={PurchaseFormModal} formProps={{ products, }} initialValues={initialValues} opensInitially={shouldAutoOpen} />
            <ExportButton
              label={false}
              outline
              fileName={`原価_${formatDate(inboundDateStart, 'yyyy-MM-dd')}_${formatDate(inboundDateEnd, 'yyyy-MM-dd')}.csv`}
              rows={getExportData}
            />
            <ImportMappingButton
              label={false}
              fields={importFields}
              onImport={handleImport}
              storageKey={`purchases-import-mapping-${tenant.id}`}
            />
          </div>
          <table className="table table-bordered">
            <thead className="thead-light text-nowrap">
              <tr>
                <th style={{ minWidth: 70 }}></th>
                <th style={{ minWidth: 250 }}>納品日</th>
                <th style={{ minWidth: 100 }}>画像</th>
                <th style={{ minWidth: 250 }}>商品</th>
                <th style={{ minWidth: 250 }}>数量</th>
                <th style={{ minWidth: 250 }}>商品原価</th>
              </tr>
            </thead>
            <tbody>
              {
                purchases.map(({ id, ref, sellerId, inboundDate, items, }) => {
                  return (
                    <Fragment key={id} id={id}>
                      {
                        items.map((item, i) => {
                          const { productId, quantity, amount, } = item;
                          const product = productsById[productId];
                          const imageUrl = product?.amazonAttributes?.summaries?.[0]?.mainImage?.link;

                          return (
                            <tr key={i}>
                              {
                                i === 0 && (
                                  <Fragment>
                                    <td rowSpan={items.length}>
                                      <div className="text-nowrap d-flex justify-content-end gap-1">
                                        <EditButton itemRef={ref} FormModal={PurchaseFormModal} formProps={{ products, }} />
                                        <DeleteButton itemRef={ref} />
                                      </div>
                                    </td>
                                    <td rowSpan={items.length}>
                                      {inboundDate && formatDate(inboundDate.toDate(), 'yyyy/MM/dd')}
                                    </td>
                                  </Fragment>
                                )
                              }
                              <td>
                                {imageUrl && (
                                  <img src={imageUrl} style={{ maxHeight: 80, maxWidth: 80 }} alt="商品画像" />
                                )}
                              </td>
                              <td>
                                [{product?.sku}] {product?.name}
                              </td>
                              <td className="text-end">
                                {numeral(quantity).format()}
                              </td>
                              <td className="text-end">
                                {numeral(amount).format()}円
                              </td>
                            </tr>
                          );
                        })
                      }
                    </Fragment>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
